import React, { useCallback, useState, useMemo } from 'react';
import UncontrolledDefaultModal from 'components/Modal/UncontrolledDefaultModal';
import { Button, Spinner } from 'reactstrap';
import { FlexTableV2 } from 'components/FlexTableV2';
import { createColumnHelper, getSortedRowModel } from '@tanstack/react-table';
import {
  fetchChatsInAttendanceByLogin,
  forceFetchChatsInAttendanceByLogin,
  CHATS_BY_AGENT_LOGIN_SLICE_NAME
} from 'store/reduce/chats-by-agent-login';
import { useDispatch, useSelector } from 'react-redux';
import WhisperModal from 'views/Monitoring/Chat/conversation/components/table/components/notificationModal/whisperModal';
import { ChatDetailsModal } from './ChatDetailsModal';

const columnHelper = createColumnHelper();

const ListConversationsModal = React.memo(({ login, name, ...props }) => {
  const [sorting, setSorting] = useState([
    {
      id: 'cliente.nome',
      desc: false
    }
  ]);
  const [columnFilters, setColumnFilters] = useState([]);

  const dispatch = useDispatch();
  const { isLoading: isChatsLoading, loginMap } = useSelector(
    (state) => state[CHATS_BY_AGENT_LOGIN_SLICE_NAME]
  );

  const chatsInAttendance = useMemo(
    () => loginMap[login]?.inAttendance ?? [],
    [loginMap, login]
  );

  const getChatsInAttendanceByLogin = useCallback(
    (login) => {
      return dispatch(fetchChatsInAttendanceByLogin({ login }));
    },
    [dispatch]
  );

  const forceRefreshChats = useCallback(
    (login) => {
      return dispatch(forceFetchChatsInAttendanceByLogin({ login }));
    },
    [dispatch]
  );

  const handleRefresh = useCallback(() => {
    forceRefreshChats(login);
  }, [login, forceRefreshChats]);

  const allFilaNames = useMemo(
    () => [
      ...new Set(chatsInAttendance.map((chat) => chat.fila).filter(Boolean))
    ],
    [chatsInAttendance]
  );

  const allApelidoCanalNames = useMemo(
    () => [
      ...new Set(
        chatsInAttendance.map((chat) => chat.apelidoCanal).filter(Boolean)
      )
    ],
    [chatsInAttendance]
  );

  const allCanalNames = useMemo(
    () => [
      ...new Set(chatsInAttendance.map((chat) => chat.canal).filter(Boolean))
    ],
    [chatsInAttendance]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('cliente.nome', {
        id: 'cliente.nome',
        header: 'Cliente',
        enableSorting: true,
        meta: {
          filterVariant: 'text'
        }
      }),
      columnHelper.accessor('fila', {
        header: 'Fila',
        enableSorting: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          return filterValue.includes(row.getValue(columnId));
        },
        meta: {
          filterVariant: 'multiselect',
          options: allFilaNames
        }
      }),
      columnHelper.accessor('cliente.telefone', {
        header: 'Telefone',
        enableSorting: true,
        meta: {
          filterVariant: 'text'
        }
      }),
      columnHelper.accessor('apelidoCanal', {
        header: 'Nome do Canal',
        enableSorting: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          return filterValue.includes(row.getValue(columnId));
        },
        meta: {
          filterVariant: 'multiselect',
          options: allApelidoCanalNames
        }
      }),
      columnHelper.accessor('canal', {
        header: 'Canal',
        enableSorting: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          return filterValue.includes(row.getValue(columnId));
        },
        meta: {
          filterVariant: 'multiselect',
          options: allCanalNames
        }
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Ações',
        cell: (cell) => {
          const chatId = cell.row.original.id;
          return (
            <div>
              {chatId && <ChatDetailsModal id={chatId} />}
              <WhisperModal
                name="Sussurrar conversa"
                {...props}
                {...cell.row.original}
              />
            </div>
          );
        }
      })
    ],
    [allFilaNames, allApelidoCanalNames, allCanalNames]
  );

  return (
    <UncontrolledDefaultModal
      trigger={
        <Button
          color="success"
          size="sm"
          title="Listar conversas em atendimento"
          onClick={() => {
            getChatsInAttendanceByLogin(login);
          }}
        >
          <i className="fas fa-comments"></i>
        </Button>
      }
      header="Conversas em Atendimento"
      title={`Conversas de ${name}`}
      size="xl"
      body={
        <>
          <div className="d-flex justify-content-end mb-3 px-4">
            <Button color="primary" size="sm" onClick={handleRefresh}>
              <i className="fas fa-sync-alt mr-2" />
              Atualizar
            </Button>
          </div>
          {isChatsLoading && chatsInAttendance.length === 0 ? (
            <div className="text-center">
              <Spinner /> <p>Buscando conversas...</p>
            </div>
          ) : !chatsInAttendance || chatsInAttendance.length === 0 ? (
            <p className="text-center">
              Nenhuma conversa em atendimento para este atendente.
            </p>
          ) : (
            <div className="px-4">
              <FlexTableV2
                table={{
                  columns: columns,
                  data: chatsInAttendance,
                  getSortedRowModel: getSortedRowModel(),
                  manualSorting: false,
                  manualFiltering: false,
                  enableLocalStorageClearing: false
                }}
                state={{
                  sorting,
                  setSorting,
                  columnFilters,
                  setColumnFilters
                }}
              />
            </div>
          )}
        </>
      }
    />
  );
});

export default ListConversationsModal;
